import {
  AssistantType,
  FlowNodeData,
  FlowNodeTypes,
  FlowNodeSetting,
} from "@/types/node";
import {RealTimeBookingRecordModel} from "@/api/models";
import React from "react";
import {ChevronDown, Info} from "lucide-react";
import {getSettingValueByKey, getSettingValueByKeys} from "@/utils/index";
import {FlowNodesData} from "../NodeTypes/NodesData";
import {highlightVariables} from "@/components/settings/SettingPrompt";
import {DynamicIcon} from "@/lib/SettingsBuilder";
import {httpModeColors} from "@/constants/index";

import {TransferCallAction} from "@/api/services";

import CalComIcon from "@/components/icons/Cal";
import GoHighLevel from "@/components/icons/GoHightLevel";
import {useReactFlow} from "@xyflow/react";
import {CustomActionRecordModel} from "@/api/models";

import {useSubflows} from "@/contexts/SubflowsContext";
import {Workflow, Zap} from "lucide-react";
import {OperatorOptions} from "@/components/settings/constants";
import {Variable} from "@/contexts/VariablesContext";
import {BaseAction} from "@/types/api";
import {cn} from "@/lib/utils";
import {BookingAction} from "@/components/settings/Actions/SelectActionsTable";

enum NodeStateType {
  NOT_FOUND = "not_found",
}
// Components
const GroupedAttributes: React.FC<{
  groupedSettings: any[];
}> = ({groupedSettings}) => {
  return (
    <div className="text-sm flex gap-2 overflow-hidden">
      {(() => {
        return groupedSettings.map(setting => {
          const numberOfParams = Array.isArray(setting.params[0].value)
            ? setting.params[0].value.length
            : 0;
          if (numberOfParams <= 0) return null;

          return (
            <div
              key={setting.title}
              className="border rounded-md p-1.5 flex items-center gap-2 hasContext text-label"
            >
              <div className="border-1 bg-primary-accent w-4 h-4">
                {setting.icon && (
                  <DynamicIcon
                    iconName={setting.icon}
                    className="h-4 w-4 mr-1.5"
                  />
                )}
              </div>
              <span className="whitespace-nowrap overflow-ellipsis overflow-hidden leading-4">
                {numberOfParams}
              </span>
            </div>
          );
        });
      })()}
    </div>
  );
};
GroupedAttributes.displayName = "GroupedAttributes";

const NodeInstructions: React.FC<{
  instructions?: string;
  defaultText?: string;
  state?: NodeStateType;
  errorMessage?: string;
}> = ({
  instructions,
  defaultText = "No instructions added...",
  state,
  errorMessage,
}) => {
  const instructionsContent = instructions
    ? highlightVariables(instructions)
    : defaultText;

  const isErrorState = state === NodeStateType.NOT_FOUND;

  return (
    <div className="flex flex-col">
      <div
        className={`text-sm overflow-hidden ${
          instructions ? "text-primary" : "text-disabled"
        }`}
      >
        <span
          className={cn(
            "line-clamp-2 overflow-hidden text-ellipsis break-words hasContext",
            isErrorState && "text-destructive flex items-center gap-2",
          )}
        >
          {isErrorState && <Info className="w-4 h-4 shrink-0" />}
          {errorMessage ? errorMessage : instructionsContent}
        </span>
      </div>
    </div>
  );
};
NodeInstructions.displayName = "NodeInstructions";

// Node Contents
const GlobalNodeContent: React.FC<{data: FlowNodeData}> = ({data}) => {
  const globalNodeAttributes = [
    "global_actions",
    "knowledge_base",
    "special_cases",
    "faq",
  ];
  const globalInstructions = getSettingValueByKey(
    data.settings ?? [],
    "global_instructions",
  );

  const globalNodeSettings = getSettingValueByKeys(
    data.settings,
    globalNodeAttributes,
  );

  return (
    <div className="flex flex-col gap-2 bg-white border rounded-lg p-3">
      <NodeInstructions
        instructions={globalInstructions as string}
        defaultText="No instructions added..."
      />
      <GroupedAttributes groupedSettings={globalNodeSettings} />
    </div>
  );
};
GlobalNodeContent.displayName = "GlobalNodeContent";

const StartNodeContent: React.FC<{data: FlowNodeData}> = ({data}) => {
  const startNodeInstructions = getSettingValueByKey(
    data.settings ?? [],
    "scripted_greeting",
  );

  return (
    <div className="flex flex-col gap-2 bg-white border rounded-lg p-3">
      <NodeInstructions
        instructions={startNodeInstructions as string}
        defaultText="No instructions added..."
      />
    </div>
  );
};
StartNodeContent.displayName = "StartNodeContent";

const CollectVariablesNodeContent: React.FC<{data: FlowNodeData}> = ({
  data,
}) => {
  const normalizeVariables = (raw: unknown): Variable[] => {
    if (!Array.isArray(raw)) return [];
    return raw.filter(v => v && typeof v === "object") as Variable[];
  };
  const variables: Variable[] = normalizeVariables(
    getSettingValueByKey(data.settings ?? [], "variables"),
  );

  const [visibleCount, setVisibleCount] = React.useState(2);
  const visibleVariables = React.useMemo(
    () => variables.slice(0, visibleCount),
    [variables, visibleCount],
  );
  const remaining = React.useMemo(
    () => Math.max(variables.length - visibleCount, 0),
    [variables.length, visibleCount],
  );

  const getVariableTypeLabel = (type: string) => {
    const variableType = OperatorOptions.find(option => option.value === type);
    return variableType?.name;
  };
  const variableTypeName = (type: string) => getVariableTypeLabel(type);
  return (
    <div className="flex flex-col gap-2">
      {variables.length === 0 ? (
        <div className="bg-white border rounded-lg p-3">
          <NodeInstructions defaultText="No variables added..." />
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {visibleVariables.map((variable, index) => (
            <div
              key={`${variable?.name}-${index}`}
              className="border rounded-lg p-3 flex flex-col gap-2.5 bg-white"
            >
              <div className="flex flex-row items-start gap-2.5">
                <div className="w-11 h-11 bg-slate border rounded-md flex items-center justify-center shrink-0">
                  <DynamicIcon
                    iconName="CaptureVariableIcon"
                    className="w-6 h-6 text-secondary"
                  />
                </div>
                <div className="flex-1 flex flex-col overflow-hidden items-start text-left">
                  <p className="text-sm text-primary font-medium overflow-hidden whitespace-nowrap text-ellipsis max-w-full">
                    {variable?.name}
                  </p>
                  <div className="flex flex-row gap-1 text-secondary font-normal text-sm overflow-hidden whitespace-nowrap text-ellipsis max-w-full text-left">
                    {variable?.type && (
                      <div className="capitalize">
                        {variableTypeName(variable?.type)}
                      </div>
                    )}
                    {(variable?.description || variable?.instructions) && (
                      <div className="relative pl-3 before:content-['•'] before:absolute before:left-0 before:text-secondary before:text-lg before:font-bold before:!leading-5 overflow-hidden whitespace-nowrap text-ellipsis">
                        {variable?.description || variable?.instructions}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
          {remaining > 0 && (
            <button
              type="button"
              className="w-full self-stretch h-9 border rounded-lg py-2 px-3 text-primary bg-white hover:bg-slate-50 transition flex items-center justify-center gap-2"
              onClick={e => {
                // e.stopPropagation();
                // e.preventDefault();
                // setVisibleCount(variables.length);
              }}
              aria-label={`Show ${remaining} more variables`}
            >
              <span>{remaining} More</span>
              <ChevronDown className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};
CollectVariablesNodeContent.displayName = "CollectVariablesNodeContent";

const JumpNodeContent: React.FC<{data: FlowNodeData}> = ({data}) => {
  const {getNode} = useReactFlow();
  const {selectedSubflowId, getSubflowById} = useSubflows();

  const jumpToStepValue = getSettingValueByKey(
    data.settings ?? [],
    "jump_to_step",
  ) as string;

  // Parse the jump_to_step value (format: "subflowId:nodeId" or just "nodeId")
  const [targetSubflowId, targetNodeId] = jumpToStepValue?.includes(":")
    ? jumpToStepValue.split(":")
    : [selectedSubflowId, jumpToStepValue];

  const isSameSubflow = targetSubflowId === selectedSubflowId;

  let displayText = "";
  let nodeType: FlowNodeTypes | null = null;
  let NodeIcon = null;
  const targetSubflowStats = {nodeCount: 0, actionCount: 0};

  if (jumpToStepValue) {
    if (isSameSubflow) {
      // Same subflow - show node title
      const jumpNode = getNode(targetNodeId);
      const isJumpToAction = jumpNode?.type
        ? jumpNode.type === FlowNodeTypes.ACTION ||
          jumpNode.type === FlowNodeTypes.TRANSFER ||
          jumpNode.type === FlowNodeTypes.BOOKING
        : false;

      let jumpToName =
        (getSettingValueByKey(
          jumpNode?.data.settings as FlowNodeSetting[],
          "name",
        ) as string) || "";

      if (isJumpToAction) {
        const jumpToAction = getSettingValueByKey(
          jumpNode?.data.settings as FlowNodeSetting[],
          "action",
        ) as CustomActionRecordModel & BaseAction;
        jumpToName = jumpToAction?.title?.length > 0 ? jumpToAction.title : "";
      }

      displayText =
        jumpToName.length > 0 ? jumpToName : jumpNode?.type || "Unknown node";

      // Get node type and icon
      if (jumpNode?.type) {
        nodeType = jumpNode.type as FlowNodeTypes;
        const nodeConfig =
          FlowNodesData[nodeType as keyof typeof FlowNodesData];
        NodeIcon = nodeConfig?.icon;
      }
    } else {
      // Different subflow - show subflow title
      const targetSubflow = getSubflowById(targetSubflowId);
      displayText = targetSubflow?.name || "Unknown subflow";
      // For different subflows, we could show a Workflow icon or similar
      nodeType = FlowNodeTypes.GLOBAL; // Use GLOBAL type icon for subflows
      const nodeConfig = FlowNodesData[nodeType as keyof typeof FlowNodesData];
      NodeIcon = nodeConfig?.icon;

      // Calculate stats for the target subflow
      if (targetSubflow) {
        const subflowNodes = targetSubflow.flowData.test.nodes;
        targetSubflowStats.nodeCount = subflowNodes.length;
        targetSubflowStats.actionCount = subflowNodes.filter(
          node =>
            node.data?.type === FlowNodeTypes.ACTION ||
            node.data?.type === FlowNodeTypes.TRANSFER ||
            node.data?.type === FlowNodeTypes.BOOKING,
        ).length;
      }
    }
  }

  return (
    <div className="flex flex-col bg-white border rounded-lg p-3">
      <div className="flex items-center justify-between gap-2 mb-1">
        <NodeInstructions
          instructions={displayText}
          defaultText="Nothing is selected"
        />
        {nodeType && NodeIcon && (
          <div className="flex items-center gap-2 mb-1">
            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded text-xs text-primary border">
              <NodeIcon className="w-3 h-3 text-primary-accent" />
              <span className="capitalize">
                {isSameSubflow ? nodeType : "Subflow"}
              </span>
            </div>
          </div>
        )}
      </div>
      {!isSameSubflow && jumpToStepValue && (
        <div className="flex items-center gap-3 text-xs text-gray-600">
          <div className="flex items-center gap-1.5">
            <Workflow className="w-3.5 h-3.5" />
            <span>{targetSubflowStats.nodeCount} nodes</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5" />
            <span>{targetSubflowStats.actionCount} actions</span>
          </div>
        </div>
      )}
    </div>
  );
};
JumpNodeContent.displayName = "JumpNodeContent";

const EndNodeContent: React.FC<{data: FlowNodeData}> = ({data}) => {
  const endNodeInstructions = getSettingValueByKey(
    data.settings ?? [],
    "instructions",
  );
  return (
    <div className="flex flex-col gap-2 bg-white border rounded-lg p-3">
      <NodeInstructions
        instructions={endNodeInstructions as string}
        defaultText="No instructions added..."
      />
    </div>
  );
};
EndNodeContent.displayName = "EndNodeContent";

const ActionNodeContent: React.FC<{data: FlowNodeData}> = ({data}) => {
  const action = getSettingValueByKey(
    data.settings ?? [],
    "action",
  ) as CustomActionRecordModel["nodeData"];

  const actionName = getSettingValueByKey(data.settings ?? [], "name");

  const isEmptyAction = !action?.action_id;
  const message = action?.start_message;

  return isEmptyAction ? (
    <div className="flex flex-col gap-2 bg-white border rounded-lg p-3">
      <NodeInstructions
        defaultText="No action selected"
        state={actionName ? NodeStateType.NOT_FOUND : undefined}
        errorMessage={
          actionName ? `Action "${actionName}" not found` : undefined
        }
      />
    </div>
  ) : (
    <>
      <div className="flex flex-col bg-white border rounded-lg p-3">
        <span className="text-xs text-secondary hasContext">Endpoint</span>
        <span className="text-sm text-primary overflow-hidden line-clamp-1 text-ellipsis break-words hasContext">
          {action?.endpoint?.url || "No endpoint"}
        </span>
        {action?.endpoint?.http_method && (
          <div
            className={`${httpModeColors[action.endpoint.http_method.toLowerCase()]} mt-2.5 mr-auto text-sm font-medium px-1.5 py-1 rounded-md`}
          >
            {action.endpoint.http_method.toUpperCase()}
          </div>
        )}
      </div>
      {message && (
        <div className="flex flex-col bg-white border rounded-lg p-3 mt-1">
          <span className="text-xs text-secondary font-medium mb-1 hasContext">
            Message
          </span>
          <span className="text-xs text-primary overflow-hidden line-clamp-2 text-ellipsis break-words hasContext">
            {message}
          </span>
        </div>
      )}
    </>
  );
};
ActionNodeContent.displayName = "ActionNodeContent";

const TransferNodeContent: React.FC<{data: FlowNodeData}> = ({data}) => {
  const action = getSettingValueByKey(
    data.settings ?? [],
    "action",
  ) as TransferCallAction & BaseAction;

  const isActionNotFound = data.actionError?.status === "not_found";
  const isEmptyAction = !action?.action_id;
  const triggerMessage = action?.call_transfer_info?.message || undefined;

  return isEmptyAction ? (
    <div className="flex flex-col gap-2 bg-white border rounded-lg p-3">
      <NodeInstructions
        defaultText="Nothing is selected"
        state={isActionNotFound ? NodeStateType.NOT_FOUND : undefined}
        errorMessage={isActionNotFound ? data.actionError?.message : undefined}
      />
    </div>
  ) : (
    <>
      <div className="flex flex-col bg-white border rounded-lg p-3">
        <span className="text-sm text-primary font-medium mb-1 hasContext">
          Human Support
        </span>
        <span className="text-xs text-primary overflow-hidden line-clamp-1 text-ellipsis break-words hasContext">
          {action?.phone_number || "No phone number"}
        </span>
      </div>
      {triggerMessage && (
        <div className="flex flex-col bg-white border rounded-lg p-3 mt-1">
          <span className="text-xs text-secondary font-medium mb-1 hasContext">
            Message
          </span>
          <span className="text-xs text-primary overflow-hidden line-clamp-2 text-ellipsis break-words hasContext">
            {triggerMessage}
          </span>
        </div>
      )}
    </>
  );
};
TransferNodeContent.displayName = "TransferNodeContent";

const BookingNodeContent: React.FC<{data: FlowNodeData}> = ({data}) => {
  const action = getSettingValueByKey(
    data.settings ?? [],
    "action",
  ) as RealTimeBookingRecordModel & BaseAction;

  const isEmptyAction = !action?.action_id;
  let formattedDate = "N/A";
  const firstApptDateStr =
    action?.config?.firstAppDate ||
    (action?.config as BookingAction["config"])?.first_appt_date;
  if (firstApptDateStr) {
    const firstApptDate = new Date(firstApptDateStr);
    if (!isNaN(firstApptDate.getTime())) {
      formattedDate =
        firstApptDate.getDate().toString().padStart(2, "0") +
        " " +
        firstApptDate.toLocaleString("default", {month: "long"}) +
        ", " +
        firstApptDate.getFullYear();
    }
  }
  const bookingType = action?.config?.type;

  const isActionNotFound = data.actionError?.status === "not_found";
  return isEmptyAction ? (
    <div className="flex flex-col gap-2 bg-white border rounded-lg p-3">
      <NodeInstructions
        defaultText="No booking action selected..."
        state={isActionNotFound ? NodeStateType.NOT_FOUND : undefined}
        errorMessage={isActionNotFound ? data.actionError?.message : undefined}
      />
    </div>
  ) : (
    <div className="flex flex-col gap-2 bg-white border rounded-lg p-3">
      <span className="text-sm text-primary font-medium mb-1 hasContext">
        Appointment
      </span>
      <div className="flex items-center gap-2 text-secondary text-sm">
        {bookingType === "calcom" ? (
          <div className="flex items-center gap-2 hasContext">
            <CalComIcon width={16} height={16} />
            Cal.com • {formattedDate}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <GoHighLevel width={16} height={16} />
            Go High Level • {formattedDate}
          </div>
        )}
      </div>
    </div>
  );
};
BookingNodeContent.displayName = "BookingNodeContent";

const EntryNodeContent: React.FC<{data: FlowNodeData}> = ({data}) => {
  // Get triggers from settings
  const triggersValue = getSettingValueByKey(data.settings, "triggers");

  // Extract trigger values and limit to max 3 items
  const triggerTexts: string[] = [];
  if (Array.isArray(triggersValue)) {
    triggersValue.forEach((group: unknown) => {
      if (Array.isArray(group)) {
        const triggerItem = group.find(
          (item: {key?: string; value?: string}) =>
            item.key === "trigger" && typeof item.value === "string",
        );
        if (triggerItem?.value) {
          triggerTexts.push(triggerItem.value);
        }
      }
    });
  }

  // Limit to max 3 items
  const displayTriggers = triggerTexts.slice(0, 3);

  return (
    <div className="flex flex-col gap-2 bg-white border rounded-lg p-3">
      {displayTriggers.length > 0 ? (
        <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
          {displayTriggers.map((trigger, index) => (
            <li key={index} className="truncate">
              {trigger}
            </li>
          ))}
        </ul>
      ) : (
        <NodeInstructions defaultText="No entry triggers added..." />
      )}
    </div>
  );
};
EntryNodeContent.displayName = "EntryNodeContent";

const SpeakNodeContent: React.FC<{data: FlowNodeData}> = ({data}) => {
  const instructions = getSettingValueByKey(
    data.settings ?? [],
    "instructions",
  ) as string;
  return (
    <div className="flex flex-col gap-2 bg-white border rounded-lg p-3">
      <NodeInstructions
        instructions={instructions}
        defaultText="No speak instructions added..."
      />
    </div>
  );
};
SpeakNodeContent.displayName = "SpeakNodeContent";

// Wrapper Component
const NodeContent: React.FC<{
  data: FlowNodeData;
  assistantType?: AssistantType;
}> = ({data}) => {
  const nodeType = data.type as FlowNodeTypes;

  return (
    <div>
      {(() => {
        switch (nodeType) {
          case FlowNodeTypes.GLOBAL:
            return <GlobalNodeContent data={data} />;
          case FlowNodeTypes.START:
            return <StartNodeContent data={data} />;
          case FlowNodeTypes.COLLECT_VARIABLES:
            return <CollectVariablesNodeContent data={data} />;
          case FlowNodeTypes.JUMP:
            return <JumpNodeContent data={data} />;
          case FlowNodeTypes.END:
            return <EndNodeContent data={data} />;
          case FlowNodeTypes.ACTION:
            return <ActionNodeContent data={data} />;
          case FlowNodeTypes.TRANSFER:
            return <TransferNodeContent data={data} />;
          case FlowNodeTypes.BOOKING:
            return <BookingNodeContent data={data} />;
          case FlowNodeTypes.ENTRY:
            return <EntryNodeContent data={data} />;
          case FlowNodeTypes.SPEAK:
            return <SpeakNodeContent data={data} />;
          default:
            return "Node Content";
        }
      })()}
    </div>
  );
};

export default NodeContent;
